
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Schedule {
  id: string;
  title: string;
  start_date: string;
  end_date: string;
  location?: string;
  notes?: string;
  assigned_guards: string[];
  team_id: string;
  created_at?: string;
  updated_at?: string;
}

export interface SchedulesQueryOptions {
  startDate?: string;
  endDate?: string;
  extendedRange?: boolean;
  selectedGuardId?: string;
  fetchAllGuardShifts?: boolean;
}

export interface GuardProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  full_name?: string;
  status?: string;
}

export const useSchedules = (teamId?: string | null, options?: SchedulesQueryOptions) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [viewType, setViewType] = useState<'week' | 'month'>('week');
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch schedules
  const {
    data: schedules = [],
    isLoading: schedulesLoading,
    error: schedulesError,
    refetch: refetchSchedules,
  } = useQuery({
    queryKey: ['schedules', teamId],
    queryFn: async (): Promise<Schedule[]> => {
      console.log('Fetching schedules for teamId:', teamId);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if user is admin
      const { data: adminCheck } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .in('role', ['admin', 'super_admin']);

      let query = supabase
        .from('team_schedules')
        .select('*');

      // If teamId is provided, filter by it
      if (teamId) {
        query = query.eq('team_id', teamId);
      } else if (!adminCheck || adminCheck.length === 0) {
        // If not admin and no specific team selected, get schedules for user's accessible teams
        const teamIds: string[] = [];

        // Get teams where user is a member
        const { data: memberTeams } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('profile_id', user.id);
        teamIds.push(...(memberTeams?.map(tm => tm.team_id) || []));

        // Get teams where user is assigned as client
        const { data: clientTeams } = await supabase
          .from('client_teams')
          .select('team_id')
          .eq('client_id', user.id);
        teamIds.push(...(clientTeams?.map(ct => ct.team_id) || []));

        // Get teams where user is a manager
        const { data: managerTeams } = await supabase
          .from('manager_teams')
          .select('team_id')
          .eq('manager_id', user.id);
        teamIds.push(...(managerTeams?.map(mt => mt.team_id) || []));

        const uniqueTeamIds = [...new Set(teamIds)];
        if (uniqueTeamIds.length > 0) {
          query = query.in('team_id', uniqueTeamIds);
        } else {
          return []; // No accessible teams
        }
      }
      // If admin and no teamId specified, fetch all schedules (no additional filter needed)

      let { data, error } = await query.order('start_date', { ascending: true });

      if (error) {
        console.error('Error fetching schedules:', error);
        throw error;
      }

      // For guards (non-admin users), filter to only show their own shifts
      if (!adminCheck || adminCheck.length === 0) {
        // Check if user is a guard (not a client or manager)
        const { data: userRoles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id);
        
        const isGuard = userRoles?.some(r => r.role === 'guard');
        
        if (isGuard) {
          // Filter schedules to only show where this guard is assigned
          data = data?.filter(schedule => 
            schedule.assigned_guards && schedule.assigned_guards.includes(user.id)
          ) || [];
          console.log(`Guard filtering: showing ${data.length} shifts assigned to guard`);
        }
      }

      console.log('Schedules fetched:', data?.length || 0);
      return data || [];
    },
  });

  // Fetch guard profiles - including archived ones for schedule display
  const {
    data: guards = [],
    isLoading: guardsLoading,
    error: guardsError,
  } = useQuery({
    queryKey: ['guard-profiles'],
    queryFn: async (): Promise<GuardProfile[]> => {
      console.log('Fetching guard profiles...');
      
      // Get current user's team access
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if user is admin
      const { data: adminCheck } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .in('role', ['admin', 'super_admin']);

      let query = supabase
        .from('profiles')
        .select('id, first_name, last_name, email, full_name, status');

      // If not admin, filter by team access
      if (!adminCheck || adminCheck.length === 0) {
        // Get user's accessible teams
        const teamIds: string[] = [];

        // Get teams where user is a member
        const { data: memberTeams } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('profile_id', user.id);
        teamIds.push(...(memberTeams?.map(tm => tm.team_id) || []));

        // Get teams where user is assigned as client
        const { data: clientTeams } = await supabase
          .from('client_teams')
          .select('team_id')
          .eq('client_id', user.id);
        teamIds.push(...(clientTeams?.map(ct => ct.team_id) || []));

        // Get teams where user is a manager
        const { data: managerTeams } = await supabase
          .from('manager_teams')
          .select('team_id')
          .eq('manager_id', user.id);
        teamIds.push(...(managerTeams?.map(mt => mt.team_id) || []));

        const uniqueTeamIds = [...new Set(teamIds)];
        
        if (uniqueTeamIds.length > 0) {
          // Get guards from accessible teams - including archived ones
          const { data: teamGuards, error } = await supabase
            .from('profiles')
            .select(`
              id, first_name, last_name, email, full_name, status,
              team_members!inner(team_id)
            `)
            .in('team_members.team_id', uniqueTeamIds);

          if (error) {
            console.error('Error fetching team guards:', error);
            throw error;
          }

          console.log('Guard profiles fetched (team-based):', teamGuards?.length || 0);
          return teamGuards || [];
        } else {
          return [];
        }
      } else {
        // Admin can see all guards including archived
        const { data, error } = await query;

        if (error) {
          console.error('Error fetching guard profiles:', error);
          throw error;
        }

        console.log('Guard profiles fetched (admin):', data?.length || 0);
        return data || [];
      }
    },
  });

  // Set up real-time subscriptions
  useEffect(() => {
    console.log('Setting up real-time subscriptions...');
    
    // Create unique channel names to avoid conflicts
    const schedulesChannelName = `schedules-changes-${Date.now()}-${Math.random()}`;
    const guardsChannelName = `guards-changes-${Date.now()}-${Math.random()}`;
    
    const schedulesChannel = supabase
      .channel(schedulesChannelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'team_schedules',
        },
        (payload) => {
          console.log('Schedule change detected:', payload);
          queryClient.invalidateQueries({ queryKey: ['schedules', teamId] });
        }
      )
      .subscribe((status) => {
        console.log('Schedules subscription status:', status);
      });

    const guardsChannel = supabase
      .channel(guardsChannelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
        },
        (payload) => {
          console.log('Guard change detected:', payload);
          queryClient.invalidateQueries({ queryKey: ['guard-profiles'] });
        }
      )
      .subscribe((status) => {
        console.log('Guards subscription status:', status);
      });

    // Listen for refetch events
    const handleRefetch = () => {
      console.log('Manual refetch requested');
      queryClient.invalidateQueries({ queryKey: ['schedules', teamId] });
      queryClient.invalidateQueries({ queryKey: ['guard-profiles'] });
    };

    document.addEventListener('refetch-schedules', handleRefetch);

    return () => {
      console.log('Cleaning up subscriptions...');
      try {
        schedulesChannel.unsubscribe();
        guardsChannel.unsubscribe();
      } catch (error) {
        console.warn('Error cleaning up subscriptions:', error);
      }
      document.removeEventListener('refetch-schedules', handleRefetch);
    };
  }, [queryClient, teamId]);

  // Helper function to get guard names
  const getGuardName = (guardId: string): string => {
    const guard = guards.find(g => g.id === guardId);
    if (!guard) return 'Unknown Guard';
    
    // Show archived status in parentheses for archived guards
    const name = guard.full_name || `${guard.first_name || ''} ${guard.last_name || ''}`.trim() || guard.email || 'Unknown';
    return guard.status === 'archived' ? `${name} (Archived)` : name;
  };

  // Helper function to get guard details
  const getGuardDetails = (guardId: string): GuardProfile | undefined => {
    return guards.find(g => g.id === guardId);
  };

  const isLoading = schedulesLoading || guardsLoading;
  const error = schedulesError || guardsError;

  if (error) {
    console.error('Error in useSchedules:', error);
    toast({
      variant: "destructive",
      title: "Error loading data",
      description: error.message || "Failed to load schedules or guards",
    });
  }

  return {
    schedules,
    guards,
    selectedDate,
    setSelectedDate,
    viewType,
    setViewType,
    isLoading,
    error,
    refetchSchedules,
    getGuardName,
    getGuardDetails,
    // Backward compatibility
    data: schedules,
    refetch: refetchSchedules,
  };
};
